import { useContext, useState, useRef, useCallback, useMemo } from "react";
import type { Job } from "../../../../models";
import { GeoJSONDataMap, GroupedGeoJSONFiles } from "../../../../utilities/types";
import { UserContext } from "../../../../hooks/UserContext";
import { ImportFormProps } from "../../shared/ImportHelpers";
import type {
	ImportFormGeoDataViewModel,
	UnmatchedFeaturesState,
	PropertyMappings,
	PropertyWithExamples,
	FeatureStats,
	FileValidationWarning,
	GeoDataViewState,
	InitialGeoData,
} from "./BulkImportForm.types";
import { DEFAULT_UNMATCHED_FEATURES } from "./BulkImportForm.types";
import { filesCount } from "./BulkImportForm.helpers";

export function useImportFormGeoDataViewModel(props: ImportFormProps): ImportFormGeoDataViewModel {
	const userContext = useContext(UserContext);

	// State
	const [loading, setLoading] = useState(false);
	const [jobData, setJobData] = useState<Job[]>([]);
	const [selectableGeoData, setSelectableGeoData] = useState<GroupedGeoJSONFiles>({});
	const [originalFiles, setOriginalFiles] = useState<File[]>([]);
	const [geoDataMap, setGeoDataMap] = useState<GeoJSONDataMap>(new Map());
	const [extractedHeaderProperties, setExtractedHeaderProperties] = useState<PropertyWithExamples[]>([]);
	const [allPropertyMappings, setAllPropertyMappings] = useState<Record<string, PropertyMappings>>({});
	const [featureStats, setFeatureStats] = useState<FeatureStats | undefined>(undefined);
	const [unmatchedFeatures, setUnmatchedFeatures] = useState<UnmatchedFeaturesState>(DEFAULT_UNMATCHED_FEATURES);
	const [fileValidationWarnings, setFileValidationWarnings] = useState<FileValidationWarning[]>([]);

	// View state (upload vs wizard)
	// Wizard is now the default entry point - FileValidation step allows adding files
	const [viewState, setViewState] = useState<GeoDataViewState>("wizard");
	const [wizardJobs, setWizardJobs] = useState<Job[]>([]);

	// Refs
	const importTableRef = useRef<HTMLDivElement>(null);

	// Computed values
	const shouldShowExtractButton = useMemo(() => {
		const boundaryCount = filesCount(selectableGeoData, "Boundary");
		return boundaryCount > 0;
	}, [selectableGeoData]);

	// Computed initialGeoData for wizard (configuration phase)
	// Always returns a valid object - wizard is the default entry point with FileValidation step
	const initialGeoData: InitialGeoData = useMemo(() => {
		// Convert originalFiles to uploadedFiles format
		const uploadedFiles = originalFiles.map((file) => ({
			name: file.name,
			path: file.name, // Using name as path for uploaded files
			size: file.size,
		}));

		return {
			warnings: fileValidationWarnings,
			uploadedFiles,
			geoDataMap,
			selectableGeoData,
			originalFiles,
		};
	}, [geoDataMap, selectableGeoData, originalFiles, fileValidationWarnings]);

	// Callback to collect property mappings from each file group
	const handlePropertyMappingsChange = useCallback((groupKey: string, mappings: PropertyMappings) => {
		setAllPropertyMappings((prev) => ({
			...prev,
			[groupKey]: mappings,
		}));
	}, []);

	// Clear handler for GeoTypeSelectorGroup
	const handleClear = useCallback(() => {
		setJobData([]);
		setSelectableGeoData({});
		setExtractedHeaderProperties([]);
		setAllPropertyMappings({});
		setUnmatchedFeatures(DEFAULT_UNMATCHED_FEATURES);
		setFileValidationWarnings([]);
	}, []);

	// Reset form handler for ImportTableWithMap
	const handleResetForm = useCallback(() => {
		setJobData([]);
		setOriginalFiles([]);
		setSelectableGeoData({});
		setGeoDataMap(new Map());
		setExtractedHeaderProperties([]);
		setAllPropertyMappings({});
		setFeatureStats(undefined);
		setUnmatchedFeatures(DEFAULT_UNMATCHED_FEATURES);
		setFileValidationWarnings([]);
	}, []);

	// Wizard handlers
	const handleWizardComplete = useCallback(
		(jobs: Job[]) => {
			// Clear wizard state - stay in wizard view (FileValidation is the entry point)
			setWizardJobs([]);
			setJobData(jobs);
			// Clear geodata so wizard shows empty FileValidation for next import
			setGeoDataMap(new Map());
			setSelectableGeoData({});
			setOriginalFiles([]);
			setFileValidationWarnings([]);
			setAllPropertyMappings({});

			// If in modal mode, notify parent and let it handle the ImportTable
			if (props.onJobsExtracted) {
				props.onJobsExtracted(jobs, featureStats);
			}
		},
		[props, featureStats],
	);

	const handleWizardCancel = useCallback(() => {
		// In modal mode, close the entire modal (X button should close modal)
		if (props.isInModal && props.onClose) {
			props.onClose();
		} else {
			// Non-modal: clear state and stay in wizard view with empty FileValidation
			setGeoDataMap(new Map());
			setSelectableGeoData({});
			setOriginalFiles([]);
			setFileValidationWarnings([]);
			setAllPropertyMappings({});
			setWizardJobs([]);
		}
	}, [props.isInModal, props.onClose]);

	const handleStartOver = useCallback(() => {
		// Clear all geodata state - wizard stays open with empty FileValidation step
		setGeoDataMap(new Map());
		setSelectableGeoData({});
		setOriginalFiles([]);
		setFileValidationWarnings([]);
		setAllPropertyMappings({});
		setWizardJobs([]);
		// Stay in wizard view - FileValidation is now the entry point
		setViewState("wizard");
	}, []);

	return {
		// State
		loading,
		jobData,
		selectableGeoData,
		originalFiles,
		geoDataMap,
		extractedHeaderProperties,
		allPropertyMappings,
		featureStats,
		unmatchedFeatures,
		fileValidationWarnings,

		// View state (upload vs wizard)
		viewState,
		wizardJobs,
		initialGeoData,

		// Computed values
		shouldShowExtractButton,

		// Context
		userContext,

		// Refs
		importTableRef,

		// State setters
		setGeoDataMap,
		setSelectableGeoData,
		setOriginalFiles,
		setLoading,
		setJobData,
		setFileValidationWarnings,

		// Event handlers
		handlePropertyMappingsChange,
		handleClear,
		handleResetForm,
		handleWizardComplete,
		handleWizardCancel,
		handleStartOver,
	};
}
